import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import type { ProductFitmentCopyJob } from '../../app/models/Product';
import { useLegacyMode } from '../legacy/LegacyModeContext';

const terminalStates = new Set(['Completed', 'CompletedWithWarnings', 'Failed', 'Cancelled']);

const showToast = (message: string) => {
    try {
        // Shopify's Toast API takes the message as its first argument and the
        // display options separately. Keep completion feedback best-effort so
        // an unavailable or changing host API can never break the app page.
        (window as any).shopify?.toast?.show?.(message, { duration: 5000 });
    } catch (error) {
        console.warn('Unable to show fitment copy completion toast.', error);
    }
};

/**
 * App-wide activity indicator for durable fitment copies. It deliberately uses
 * polling rather than an in-memory event channel so navigation and API restarts
 * cannot disconnect the UI from the persisted job state.
 */
const ProductFitmentCopyActivity = () => {
    const { loading, status } = useLegacyMode();
    // Wait for a confirmed native store before starting native-only requests.
    // Legacy pilot sessions deliberately cannot access fitment-copy jobs.
    const canPoll = !loading && status?.storefrontMode?.toLowerCase() === 'native';
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<ProductFitmentCopyJob[]>([]);
    const previousStates = useRef<Map<number, string>>(new Map());
    const initialized = useRef(false);

    useEffect(() => {
        if (!canPoll) return;
        let disposed = false;
        let timeoutId: number | undefined;

        const poll = async () => {
            let hasActiveJobs = false;
            try {
                const nextJobs = await agent.Products.listRecentFitmentCopyJobs(false, 10);
                if (disposed) return;

                hasActiveJobs = nextJobs.some((job) => job.isActive);
                if (initialized.current) {
                    nextJobs.forEach((job) => {
                        const previousState = previousStates.current.get(job.id);
                        if (previousState && !terminalStates.has(previousState) && terminalStates.has(job.state)) {
                            showToast(job.state === 'Completed'
                                ? `${job.totalFitments.toLocaleString()} fitments copied to ${job.targetProductTitle}.`
                                : job.message);
                        }
                    });
                }

                previousStates.current = new Map(nextJobs.map((job) => [job.id, job.state]));
                initialized.current = true;
                setJobs(nextJobs);
            } catch {
                // Product pages surface actionable API errors. The global status
                // indicator stays quiet during transient polling failures.
            } finally {
                if (!disposed) {
                    const delay = document.visibilityState === 'hidden'
                        ? 15000
                        : hasActiveJobs ? 3000 : 15000;
                    timeoutId = window.setTimeout(poll, delay);
                }
            }
        };

        poll();
        return () => {
            disposed = true;
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, [canPoll]);

    const activeJobs = useMemo(() => jobs.filter((job) => job.isActive), [jobs]);
    if (!canPoll || activeJobs.length === 0) return null;

    const job = activeJobs[0];
    // Floating status card; Polaris has no toast with progress, so only the
    // positioning is custom and the card itself is native.
    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                right: 24,
                bottom: 88, // Leave room for the app-wide support launcher.
                width: 'min(380px, calc(100vw - 32px))',
                zIndex: 480,
                boxShadow: '0 8px 28px rgba(0, 0, 0, 0.18)',
                borderRadius: 12,
            }}
        >
            <s-box background="base" borderRadius="large" padding="base">
                <s-stack gap="small-200">
                    <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
                        <s-heading>Copying fitments</s-heading>
                        <s-badge tone="info">{activeJobs.length > 1 ? `${activeJobs.length} running` : job.state}</s-badge>
                    </s-stack>
                    <s-text>{job.sourceProductTitle} → {job.targetProductTitle}</s-text>
                    <s-progress accessibilityLabel="Copy progress" value={job.progressPercent} max={100} />
                    <s-text color="subdued">{job.message}</s-text>
                    <s-stack direction="inline">
                        <s-link onClick={() => navigate(`/products/${job.targetProductId}`)}>View product</s-link>
                    </s-stack>
                </s-stack>
            </s-box>
        </div>
    );
};

export default ProductFitmentCopyActivity;
